import {
  AppIdPayload,
  Datenhaltung,
  GlobalAction,
  GlobalActions,
  InitializeShopPayload,
  isAzam,
  ModalData,
  Settings,
  ShowModal,
} from '@ps20-frontend/shop-integration-redux';
import {
  AVLadeVorgangResponse,
  EinstiegResponse,
  Kooperation,
  Role,
  Vermittlernummer,
} from '@ps20/bff-models';
import { FeatureToggles } from '@ps20-frontend/frontend-models';
import { AnyAction } from 'redux';
import cloneDeep from 'lodash/cloneDeep';
import { AppConfigLoadedAction, ItemCachedAction, LocalActions } from './local.actions';

export interface TransientState {
  datenhaltung?: Datenhaltung;
  responseCache: {
    items: Array<{ hash: string; value: unknown }>;
  };
  role: Role;
  isAnonToken?: boolean;
  accessToken?: string;
  avLoaded: boolean;
  appIsInitialized: boolean;
  appSourcePathConfigs: { [appName: string]: string };
  appVorbelegungPending: { [appName: string]: boolean };
  errorPageOpen: boolean;
  errorPageTextIds: string | string[];
  errorPageZusatzTexte?: string[];
  errorPageParams?: { [key: string]: string };
  sessionExpired: boolean;
  userLoggedOut: boolean;
  constructionPageOpen: boolean;
  forwardedToUnderwriterPageOpen: boolean;
  loadVorvertraege: {
    loadedOrSkipped: boolean;
    kundennummerReceived: boolean;
    kundennummer: string;
    vermittlernummer: Vermittlernummer;
  };
  featureToggles: FeatureToggles;
  kooperationen?: Kooperation[];
  modal: ModalData;
  testDaten?: string;
  isInkasso?: boolean;
  vnAddressInvalid: {
    open: boolean;
    einstiegResponse?: EinstiegResponse;
    invalidAddressConfirmed: boolean;
  };
  settings: Settings;
  isSdwSession?: boolean;
}

export function transientStateReducer(
  sectionState: TransientState = {} as TransientState,
  action: AnyAction,
): TransientState {
  switch (action.type) {
    case LocalActions.APP_CONFIG_LOADED: {
      const loadedAction: AppConfigLoadedAction = action;
      return {
        ...sectionState,
        appSourcePathConfigs: {
          ...sectionState.appSourcePathConfigs,
          [loadedAction.payload.appId]: loadedAction.payload.filename,
        },
      };
    }
    case GlobalActions.VORBELEGUNG_REQUESTED: {
      const loadedAction: GlobalAction<AppIdPayload> = action;
      return {
        ...sectionState,
        appVorbelegungPending: {
          ...sectionState.appVorbelegungPending,
          [loadedAction.payload.appId]: true,
        },
      };
    }
    case GlobalActions.VORBELEGUNG_FINISHED: {
      const loadedAction: GlobalAction<AppIdPayload> = action;
      return {
        ...sectionState,
        appVorbelegungPending: {
          ...sectionState.appVorbelegungPending,
          [loadedAction.payload.appId]: false,
        },
      };
    }
    case LocalActions.APP_IS_INITIALIZED: {
      return {
        ...sectionState,
        appIsInitialized: true,
      };
    }
    case GlobalActions.ERROR_PAGE_REQUESTED: {
      const textIds = action.payload ? action.payload.errorTextId : null;
      const zusatzInfo = action.payload ? action.payload.zusatzInfo : null;
      const params = action.payload ? action.payload.params : null;
      return {
        ...sectionState,
        appIsInitialized: true,
        errorPageOpen: true,
        errorPageTextIds: textIds,
        errorPageZusatzTexte: zusatzInfo,
        errorPageParams: params,
      };
    }
    case GlobalActions.USER_LOGGED_OUT: {
      return {
        ...sectionState,
        userLoggedOut: true,
      };
    }
    case GlobalActions.OPEN_FORWARDED_TO_UNDERWRITER_PAGE: {
      return {
        ...sectionState,
        forwardedToUnderwriterPageOpen: true,
      };
    }
    case GlobalActions.CLOSE_FORWARDED_TO_UNDERWRITER_PAGE: {
      return {
        ...sectionState,
        forwardedToUnderwriterPageOpen: false,
      };
    }
    case LocalActions.SESSION_EXPIRED: {
      return {
        ...sectionState,
        sessionExpired: true,
      };
    }
    case GlobalActions.CONSTRUCTION_PAGE_REQUESTED: {
      return {
        ...sectionState,
        constructionPageOpen: true,
      };
    }
    case GlobalActions.SHOW_MODAL: {
      const actionPayload: ShowModal = action.payload;
      return {
        ...sectionState,
        modal: {
          modalOpen: true,
          textContent: actionPayload.textContent,
          styleObject: actionPayload.styleObject,
          disableClose: actionPayload.disableClose,
        },
      };
    }
    case GlobalActions.CLOSE_MODAL: {
      return {
        ...sectionState,
        modal: { ...sectionState.modal, modalOpen: false },
      };
    }
    case LocalActions.VN_ADDRESS_INVALID_PAGE_REQUESTED: {
      return {
        ...sectionState,
        vnAddressInvalid: {
          ...sectionState.vnAddressInvalid,
          open: true,
        },
      };
    }
    case LocalActions.VN_ADDRESS_INVALID: {
      return {
        ...sectionState,
        vnAddressInvalid: {
          ...sectionState.vnAddressInvalid,
          open: true,
          einstiegResponse: action.payload,
        },
      };
    }
    case LocalActions.VN_ADDRESS_INVALID_CONFIRMED: {
      return {
        ...sectionState,
        vnAddressInvalid: {
          ...sectionState.vnAddressInvalid,
          open: false,
          invalidAddressConfirmed: true,
        },
      };
    }
    case GlobalActions.EINSTIEG_REQUEST_SUCCESSFUL: {
      const newState = { ...sectionState };
      const actionPayload: EinstiegResponse = action.payload;
      newState.loadVorvertraege.kundennummer = actionPayload.versicherungsnehmer.kundennummer;
      newState.loadVorvertraege.vermittlernummer = actionPayload.vertreter.defaultVermittlernummer;
      newState.loadVorvertraege.kundennummerReceived = true;
      newState.isInkasso = actionPayload.vertreter.isInkasso;
      newState.vnAddressInvalid.einstiegResponse = null;
      return newState;
    }
    case GlobalActions.LOAD_AV_REQUEST_SUCCESSFUL: {
      const newState = { ...sectionState };
      newState.avLoaded = true;
      newState.datenhaltung.av.angebotsBuendelId = (action.payload as AVLadeVorgangResponse).angebotsBuendelId;
      return newState;
    }
    case GlobalActions.AUTHENTICATION_SUCCESSFUL: {
      return {
        ...sectionState,
        datenhaltung: {
          kdv: sectionState.datenhaltung.kdv,
          av: sectionState.datenhaltung.av,
          sdw: sectionState.datenhaltung.sdw,
          auth: {
            ...sectionState.datenhaltung.auth,
            authenticated: true,
          },
          permasession: sectionState.datenhaltung.permasession,
          azamVorgangsId: sectionState.datenhaltung.azamVorgangsId,
        },
      };
    }
    case GlobalActions.FEATURE_TOGGLE_RECEIVED: {
      const actionPayload: FeatureToggles = action.payload;
      return {
        ...sectionState,
        featureToggles: actionPayload,
      };
    }
    case GlobalActions.UPDATE_VORVERTRAEGE:
    case GlobalActions.UPDATE_VORVERTRAEGE_FAILED:
    case GlobalActions.VORVERTRAEGE_LOAD_FINISHED: {
      const newState = { ...sectionState };
      newState.loadVorvertraege.loadedOrSkipped = true;
      return newState;
    }
    case GlobalActions.AUTHENTICATION_FAILED: {
      return {
        ...sectionState,
        datenhaltung: {
          ...sectionState.datenhaltung,
          auth: {
            ...sectionState.datenhaltung.auth,
            authenticated: false,
          },
        },
      };
    }
    case GlobalActions.ROLE_DETERMINED: {
      return {
        ...sectionState,
        role: action.payload.role,
        datenhaltung: {
          ...sectionState.datenhaltung,
          auth: {
            ...sectionState.datenhaltung.auth,
          },
          azamVorgangsId:
            isAzam(action.payload.role) && !sectionState.datenhaltung.azamVorgangsId
              ? sectionState.datenhaltung.kdv.vorId
              : sectionState.datenhaltung.azamVorgangsId,
        },
      };
    }
    case GlobalActions.SET_ACCESS_TOKEN: {
      return {
        ...sectionState,
        accessToken: action.payload,
        datenhaltung: {
          ...sectionState.datenhaltung,
          sdw: {
            ...sectionState.datenhaltung.sdw,
            accessToken: action.payload,
          },
        },
      };
    }
    case GlobalActions.SHOP_INIT: {
      const actionPayload: InitializeShopPayload = action.payload;
      const { kdv, av, sdw, azamVorgangsId, permasession } = actionPayload;
      const newState = {
        ...sectionState,
        role: action.payload.role,
        isSdwSession: ![null, undefined].includes(actionPayload.sdw?.partyId),
        datenhaltung: {
          auth: {
            ...sectionState.datenhaltung.auth,
          },
          kdv,
          av,
          sdw: {
            ...sectionState.datenhaltung.sdw,
            partyId: sdw?.partyId,
            role: sdw?.role,
            code: sdw?.code,
            state: sdw?.state,
            accountManager: sdw?.accountManager,
          },
          permasession,
          azamVorgangsId,
        },
      };
      if (actionPayload.testDaten) {
        newState.testDaten = actionPayload.testDaten;
      }

      return newState;
    }
    case GlobalActions.SAVE_STATUS_RECEIVED: {
      const newState: TransientState = cloneDeep(sectionState);
      newState.datenhaltung.kdv.copyFlag = 0;
      newState.avLoaded = true;
      newState.datenhaltung.av.cryptId = action.payload.cryptId;
      newState.datenhaltung.av.angebotsBuendelId = action.payload.angebotsBuendelId;
      return { ...newState };
    }
    case LocalActions.ITEM_CACHED: {
      const itemCachedAction: ItemCachedAction = action;
      const newItemCache = sectionState.responseCache.items.map(elem => {
        return { hash: elem.hash, value: elem.value };
      });
      newItemCache.push({
        hash: itemCachedAction.payload.hashedData,
        value: itemCachedAction.payload.data,
      });

      const responseCache = {
        items: [...newItemCache],
      };
      return {
        ...sectionState,
        responseCache,
      };
    }
    case GlobalActions.SETTINGS_RECEIVED:
      return {
        ...sectionState,
        settings: action.payload,
      };
    case GlobalActions.KOOPERATIONEN_LIST_RECEIVED: {
      return {
        ...sectionState,
        kooperationen: action.payload,
      };
    }
    default:
      return sectionState;
  }
}
